import { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { ReactComponent as NewWindowIcon } from 'assets/new-window.svg';

import { DESIGN_REMIX_STATUS } from 'utlis/constants';

import GenerationOverlay from './GenerationOverlay';

import styles from './styles.module.scss';

const PORTRAIT_FALLBACK_W = 402;
const PORTRAIT_FALLBACK_H = 874;
const LANDSCAPE_FALLBACK_W = 874;
const LANDSCAPE_FALLBACK_H = 402;

const PrototypeSideBySidePanel = ({
  remixStatus,
  previewSrc,
  htmlUrl,
  processUuid,
  variantIndex,
  refinementPrompt,
  isDismissed,
  isDismissing,
  isOwnerView,
  isReviewOwner,
  onDismiss,
  renderedDimensions,
  isLandscape,
  onGenerate,
  isGenerateDisabled,
  generateDisabledReason,
  isGenerateBusy,
  ctaTitle,
  ctaSubtitle,
  ctaButtonLabel,
}) => {
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);

  const isIdleCta = remixStatus == null && typeof onGenerate === 'function';
  const isFailedCta = remixStatus === DESIGN_REMIX_STATUS.FAILED && typeof onGenerate === 'function';

  // Only the review owner sees generate/retry CTAs — no variant exists yet for others to view
  if (!isReviewOwner && (isIdleCta || isFailedCta)) {
    return null;
  }

  if (remixStatus == null && !isIdleCta) {
    return null;
  }

  const variantLabel = `Variant ${String.fromCharCode(65 + variantIndex)}`;

  const fallbackW = isLandscape ? LANDSCAPE_FALLBACK_W : PORTRAIT_FALLBACK_W;
  const fallbackH = isLandscape ? LANDSCAPE_FALLBACK_H : PORTRAIT_FALLBACK_H;
  const panelW = renderedDimensions?.width ?? fallbackW;
  const panelH = renderedDimensions?.height ?? fallbackH;
  const panelSizeStyle = { width: panelW, height: panelH, flexShrink: 0 };
  const panelFrameSizeStyle = { width: panelW + 32, height: panelH + 40, flexShrink: 0 };

  const isInProgress = remixStatus === DESIGN_REMIX_STATUS.IN_PROGRESS;
  const isCompleted = remixStatus === DESIGN_REMIX_STATUS.COMPLETED;
  const resolvedCtaTitle = ctaTitle || 'Use the screen’s feedback to make a new variant';
  const resolvedCtaButtonLabel = ctaButtonLabel || 'Generate variant';

  const handleOpenPrototype = () => {
    if (!processUuid) {
      return;
    }

    const baseURL = `${process.env.REACT_APP_DESIGN_PRO_BASE_URL || window.location.origin}preview-share/`;

    window.open(baseURL + processUuid, '_blank', 'noopener,noreferrer');
  };

  const showDismissButton = isOwnerView && isReviewOwner && isCompleted && processUuid;

  if (isIdleCta || isFailedCta) {
    return (
      <div className={cn(styles['prototype-side-panel'], isLandscape && styles['prototype-side-panel--landscape'])}>
        <div className={styles['prototype-panel-header']}>
          <p className={cn(styles['screen-label'], styles['screen-label--prototype'])}>
            Variant
          </p>
        </div>

        <div
          className={styles['prototype-screen-wrapper']}
          style={panelFrameSizeStyle}
        >
          <div
            className={cn(
              styles['prototype-image-wrapper'],
              styles['prototype-image-wrapper--cta'],
              isFailedCta && styles['prototype-image-wrapper--failed']
            )}
            style={panelSizeStyle}
          >
            <div className={styles['prototype-cta']}>
              <p
                className={cn(
                  styles['prototype-cta-title'],
                  isFailedCta && styles['prototype-cta-title--failed']
                )}
              >
                {resolvedCtaTitle}
              </p>
              {ctaSubtitle && (
                <p className={styles['prototype-cta-subtitle']}>
                  {ctaSubtitle}
                </p>
              )}
              <button
                type="button"
                className={styles['prototype-cta-btn']}
                onClick={onGenerate}
                disabled={isGenerateDisabled || isGenerateBusy}
              >
                {isGenerateBusy ? 'Generating…' : resolvedCtaButtonLabel}
              </button>
              {isGenerateDisabled && generateDisabledReason && (
                <p className={styles['prototype-cta-disabled-reason']}>
                  {generateDisabledReason}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles['prototype-side-panel'], isLandscape && styles['prototype-side-panel--landscape'])}>
      <div className={styles['prototype-panel-header']}>
        <p className={cn(styles['screen-label'], styles['screen-label--prototype'])}>
          {variantLabel}
        </p>

        {showDismissButton && (
          <button
            type="button"
            className={styles['variant-dismiss-btn']}
            onClick={() => onDismiss?.(processUuid, !isDismissed)}
            disabled={isDismissing}
            aria-label={isDismissed ? `Put back ${variantLabel}` : `Dismiss ${variantLabel}`}
          >
            {isDismissing
              ? <span className={styles['variant-dismiss-loader']} />
              : (isDismissed ? 'Put back' : 'Dismiss')}
          </button>
        )}
      </div>

      <div
        className={cn(
          styles['prototype-screen-wrapper'],
          isInProgress && styles['prototype-motion-enter'],
          isInProgress && isLandscape && styles['prototype-motion-enter--landscape']
        )}
        style={panelFrameSizeStyle}
      >
        {isInProgress && (
          <GenerationOverlay width={panelW} height={panelH} />
        )}

        {isCompleted && previewSrc && (
          <div
            className={cn(
              styles['prototype-image-wrapper'],
              isDismissed && styles['prototype-image-wrapper--dismissed']
            )}
            style={panelSizeStyle}
          >
            <img
              src={previewSrc}
              alt={`${variantLabel} preview`}
              className={styles['prototype-image']}
              key={processUuid}
            />

            {isDismissed && (
              <div className={styles['variant-dismissed-overlay']}>
                <p className={styles['variant-dismissed-label']}>
                  Variant hidden in the audit&apos;s shared link
                </p>
              </div>
            )}

            {!isDismissed && refinementPrompt && (
              <div className={styles['refinement-overlay']}>
                <div className={styles['left']}>
                  <span className={styles['label']}>
                    Refinement
                  </span>
                  <button
                    type="button"
                    className={cn(
                      styles['text'],
                      !isPromptExpanded && styles['text--collapsed']
                    )}
                    onClick={() => setIsPromptExpanded(prev => !prev)}
                    title={!isPromptExpanded ? refinementPrompt : undefined}
                  >
                    {`"${refinementPrompt}"`}
                  </button>
                </div>
                {htmlUrl && (
                  <button
                    type="button"
                    className={styles['open-btn']}
                    onClick={handleOpenPrototype}
                    aria-label={`Open ${variantLabel} in new window`}
                  >
                    <NewWindowIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {htmlUrl && (!refinementPrompt || isDismissed) && (
              <button
                type="button"
                className={cn(
                  styles['prototype-open-btn'],
                  isDismissed && styles['prototype-open-btn--above-overlay']
                )}
                onClick={handleOpenPrototype}
                aria-label={`Open ${variantLabel} in new window`}
              >
                <NewWindowIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

PrototypeSideBySidePanel.propTypes = {
  remixStatus: PropTypes.number,
  previewSrc: PropTypes.string,
  htmlUrl: PropTypes.string,
  processUuid: PropTypes.string,
  variantIndex: PropTypes.number,
  refinementPrompt: PropTypes.string,
  isDismissed: PropTypes.bool,
  isDismissing: PropTypes.bool,
  isOwnerView: PropTypes.bool,
  isReviewOwner: PropTypes.bool,
  onDismiss: PropTypes.func,
  renderedDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  isLandscape: PropTypes.bool,
  onGenerate: PropTypes.func,
  isGenerateDisabled: PropTypes.bool,
  generateDisabledReason: PropTypes.string,
  isGenerateBusy: PropTypes.bool,
  ctaTitle: PropTypes.string,
  ctaSubtitle: PropTypes.string,
  ctaButtonLabel: PropTypes.string,
};

PrototypeSideBySidePanel.defaultProps = {
  remixStatus: null,
  previewSrc: null,
  htmlUrl: null,
  processUuid: null,
  variantIndex: 0,
  refinementPrompt: null,
  isDismissed: false,
  isDismissing: false,
  isOwnerView: false,
  isReviewOwner: false,
  onDismiss: null,
  renderedDimensions: null,
  isLandscape: false,
  onGenerate: null,
  isGenerateDisabled: false,
  generateDisabledReason: null,
  isGenerateBusy: false,
  ctaTitle: null,
  ctaSubtitle: null,
  ctaButtonLabel: null,
};

export default PrototypeSideBySidePanel;
